import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@resy/ui-kitchen';
import PauseIcon from '../../../images/amexrga/pause.svg';
import PlayIcon from '../../../images/amexrga/play.svg';
import { handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import { CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import './ResyRewardsBackgroundVideo.scss';

const prefersReducedMotion = () => typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const playVideo = (video) => {
  const playPromise = video.play();
  if (playPromise) playPromise.catch(() => {});
};

const ResyRewardsBackgroundVideo = ({
  eventAnalytics,
  matchMedia,
  posterUrl,
  videoConfig,
  videoUrlPrefix,
}) => {
  const {
    desktop,
    mobile,
    play_label: playLabel = 'Play background video',
    pause_label: pauseLabel = 'Pause background video',
  } = videoConfig;

  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoName = matchMedia.md ? desktop : mobile;
  const videoSrc = `${videoUrlPrefix}${videoName}`;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // React does not reliably reflect the muted attribute, and browsers only autoplay muted video.
    video.muted = true;
    if (!prefersReducedMotion()) playVideo(video);
  }, [videoSrc]);

  const onToggle = () => {
    const video = videoRef.current;
    handleMktAnalytics(eventAnalytics, CTA_EVENT_NAME, {
      action_name: isPlaying ? 'Banner Video - Pause Click' : 'Banner Video - Play Click',
    });
    if (isPlaying) {
      video.pause();
    } else {
      playVideo(video);
    }
  };

  return (
    <div className="ResyRewardsBackgroundVideo">
      <video
        ref={videoRef}
        className="ResyRewardsBackgroundVideo__video"
        src={videoSrc}
        poster={posterUrl || undefined}
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      <Button
        variant="no-style"
        type="button"
        className="ResyRewardsBackgroundVideo__toggle"
        aria-label={isPlaying ? pauseLabel : playLabel}
        onClick={onToggle}
      >
        <i className="ResyIcon" aria-hidden="true">
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </i>
      </Button>
    </div>
  );
};

ResyRewardsBackgroundVideo.propTypes = {
  eventAnalytics: PropTypes.func,
  matchMedia: PropTypes.shape({
    md: PropTypes.bool,
  }).isRequired,
  posterUrl: PropTypes.string,
  videoConfig: PropTypes.shape({
    desktop: PropTypes.string.isRequired,
    mobile: PropTypes.string.isRequired,
    play_label: PropTypes.string,
    pause_label: PropTypes.string,
  }).isRequired,
  videoUrlPrefix: PropTypes.string.isRequired,
};

ResyRewardsBackgroundVideo.defaultProps = {
  eventAnalytics: null,
  posterUrl: '',
};

export default ResyRewardsBackgroundVideo;
